import React, { useState, useRef, useEffect } from 'react';

import AddressVerificationForm from '../common/addressVerificationForm';
import ShipToAddress from '../common/shipToAddress';
import { useDispatch, useSelector } from 'react-redux';
import { getAddressList, getAddressVerificationData, personShipTo, saveAddressList, saveAddressVerificationData, updateSelectedAddress } from '../../redux/actions';
import { addressValidationErrorCode } from '../../redux/util/addressValidationErrorCodes';

const newShipToAddressOverlayDialog = props => {
	const [screens, setScreens] = useState(() => {
		const defaultAddress =
			props.address && Object.keys(props.address).length
				? {
						country: props.address.COUNTRY,
						name: props.context === 'NCRP' ? props.address.NAME : '',
						companyName: props.context === 'NCRP' ? props.address.NAME : '',
						addressLine1: props.address.ADDRESS1,
						addressLine2: props.address.ADDRESS2,
						addressLine3: props.address.ADDRESS3,
						city: props.address.CITY,
						state: props.address.STATE,
						province: props.address.PROVINCE,
						zipCode: props.address.POSTALCODE,
						postalCode: props.address.POSTALCODE
				  }
				: { country: props.limitNewShipToAddressTo === 'CANADA' ? 'CA' : 'US' };

		return [
			{
				title: `Add New Shipping Address${props.limitNewShipToAddressTo ? ` - ${props.limitNewShipToAddressTo}` : ''}`,
				data: defaultAddress
			},
			{
				title: 'Verify Shipping Address',
				data: null
			}
		];
	});
	const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
	const [selectedAddress, setSelectedAddress] = useState();
	const [errorMessages, setErrorMessages] = useState([]);

	const addressRef = useRef();
	const enteredAddressRef = useRef();

	const { user, registration } = useSelector(({ user, registration }) => ({ user, registration }));
	const { addressVerificationData } = registration || {};

	const dispatch = useDispatch();

	useEffect(() => {
		if (selectedAddress === 'asEntered') {
			setScreens(screens => [...screens, { title: 'Confirm Address' }]);
		} else if (screens.length === 3) {
			setScreens(screens => screens.slice(0, 2));
		}
	}, [selectedAddress]);

	const updateAddressFieldsFromFoundAddress = () => {
		let data = screens[0].data;

		const addressLine1 = addressVerificationData.responseObject.addressline1.trim(),
			addressLine2 = addressVerificationData.responseObject.addressline2.trim(),
			addressLine3 = (addressVerificationData.responseObject.addressline3 || '').trim(),
			city = addressVerificationData.responseObject.city.trim(),
			state = addressVerificationData.responseObject.state.trim(),
			zipCode = addressVerificationData.responseObject.postalcode.trim(),
			postalCode = addressVerificationData.responseObject.postalcode.trim();

		data = {
			...data,
			addressLine1,
			addressLine2,
			addressLine3,
			city,
			state,
			zipCode,
			postalCode
		};

		setScreens(screens => screens.map((screen, index) => (index === 0 ? { ...screen, data: { ...data } } : screen)));

		return data;
	};

	const handleNewAddress = formData => {
		if (!props.skipAddressValidation && ['US', 'CA'].includes(formData.country)) {
			const body = { ...formData };

			body.state = formData.country === 'CA' ? body.province : body.state;
			body.zipCode = formData.country === 'CA' ? body.postalCode : body.zipCode;
			const queryString =
				'address1=' +
				encodeURIComponent(body.addressLine1) +
				'&address2=' +
				encodeURIComponent(body.addressLine2) +
				'&city=' +
				body.city +
				'&state=' +
				body.state +
				'&zip=' +
				body.zipCode +
				'&country=' +
				body.country +
				'&requestType=validateAddress';

			dispatch(
				getAddressVerificationData(queryString, addressVerificationResponse => {
					const errorCodes =
						addressVerificationResponse &&
						addressVerificationResponse.responseObject &&
						addressVerificationResponse.responseObject.results &&
						addressVerificationResponse.responseObject.results.split(',');
					let errorMessages = [];
					errorCodes &&
						Object.entries(addressValidationErrorCode).forEach(([key, val]) => {
							if (key === errorCodes[0]) {
								errorMessages.push(val);
							}
						});

					setScreens(screens => screens.map((screen, index) => (index === 0 ? { ...screen, data: { ...formData } } : screen)));
					setCurrentScreenIndex(1);
					setSelectedAddress(
						addressVerificationResponse.status && addressVerificationResponse.responseObject && !addressVerificationResponse.responseObject.grouponecallerror
							? 'asCorrected'
							: 'asEntered'
					);
					setErrorMessages(errorMessages);
				})
			);
		} else {
			if (props.skipAddressValidation) {
				enteredAddressRef.current = { ...formData };
				createShipToAddress();
			} else {
				dispatch(saveAddressVerificationData(null));
				setScreens(screens => screens.map((screen, index) => (index === 0 ? { ...screen, data: { ...formData } } : screen)));
				setCurrentScreenIndex(1);
				setSelectedAddress('asEntered');
				setErrorMessages([]);
			}
		}
	};

	const handleAddressSelection = e => {
		const selectedAddress = e.target.value;
		setSelectedAddress(selectedAddress);
	};

	const handleEditAddress = (selectedAddress, event) => {
		event.preventDefault();

		if (selectedAddress === 'asCorrected') {
			updateAddressFieldsFromFoundAddress();
		}

		setCurrentScreenIndex(0);
	};

	const handleBack = () => setCurrentScreenIndex(currentScreenIndex => currentScreenIndex - 1);

	const handleSubmit = event => {
		event.preventDefault();
		addressRef.current.validateFields(handleNewAddress);
	};

	const updateConsumerAddress = (address, personShipToDetails) => {
		const selectedAddress = {
			NAME: `${address.firstName || ''}${address.lastName ? ` ${address.lastName}` : ''}`,
			ADDRESS1: address.address1,
			ADDRESS2: address.address2,
			ADDRESS3: address.address3,
			CITY: address.city,
			STATE: address.state,
			PROVINCE: address.province,
			COUNTY: address.county,
			COUNTRY: address.country,
			POSTALCODE: address.postalCode,
			STATUS: 'A',
			PURPOSE: 'SHIP_TO',
			SITE_USE_ID: personShipToDetails.siteUseId,
			SHIP_TO_ACCOUNT_ID: personShipToDetails.custAccountId,
			PURPOSE_STATUS: 'A',
			LOCATION: props.context === 'NCRP' ? 'NEW_SHIP_TO_NCRP' : 'NEW_SHIP_TO_PNET'
		};

		const addressList = JSON.parse(JSON.stringify(user.addressList));
		addressList.responseObject.push(selectedAddress);

		dispatch(saveAddressList(addressList));
		dispatch(updateSelectedAddress(JSON.stringify(selectedAddress)));
		if (selectedAddress.POSTALCODE) {
			const zipCode = selectedAddress.POSTALCODE.split('-')[0];
			props.saveCustomZipCode(zipCode);
		}
		props.onClose();
	};

	const updateShipToAddress = (url, locationId, retryLimit) => {
		dispatch(
			getAddressList(url, true, addressListResponse => {
				if (addressListResponse.status && addressListResponse.responseObject) {
					const foundAddress = addressListResponse.responseObject.find(
						address =>
							address.LOCATION === locationId.toString() &&
							address.PURPOSE === 'SHIP_TO' &&
							address.STATUS === 'A' &&
							(!address.PURPOSE_STATUS || address.PURPOSE_STATUS === 'A')
					);

					if (foundAddress) {
						const addressList = JSON.parse(JSON.stringify(user.addressList));
						addressList.responseObject.push(foundAddress);

						dispatch(saveAddressList(addressList));
						dispatch(updateSelectedAddress(JSON.stringify(foundAddress)));
						if (foundAddress.POSTALCODE) {
							const zipCode = foundAddress.POSTALCODE.split('-')[0];
							props.saveCustomZipCode(zipCode);
						}
						props.onClose();
					} else {
						if (retryLimit) {
							updateShipToAddress(url, locationId, retryLimit - 1);
						} else {
							props.onClose();
						}
					}
				} else {
					if (retryLimit) {
						updateShipToAddress(url, locationId, retryLimit - 1);
					} else {
						props.onClose();
					}
				}
			})
		);
	};

	const createShipToAddress = () => {
		let shipToAddress = {};
		const enteredAddress = props.skipAddressValidation ? enteredAddressRef.current : screens[0].data;

		if (selectedAddress === 'asEntered' || props.skipAddressValidation) {
			shipToAddress = {
				...shipToAddress,
				addressLine1: enteredAddress.addressLine1,
				addressLine2: enteredAddress.addressLine2,
				addressLine3: enteredAddress.addressLine3,
				city: enteredAddress.city,
				country: enteredAddress.country
			};

			if (enteredAddress.country === 'US') {
				shipToAddress.state = enteredAddress.state;
				shipToAddress.zip = enteredAddress.zipCode;
				shipToAddress.postalCode = enteredAddress.zipCode;
			} else if (enteredAddress.country === 'CA') {
				shipToAddress.state = enteredAddress.province;
				shipToAddress.province = enteredAddress.province;
				shipToAddress.zip = enteredAddress.postalCode;
				shipToAddress.postalCode = enteredAddress.postalCode;
			} else {
				shipToAddress.state = enteredAddress.state;
				shipToAddress.province = enteredAddress.province;
				shipToAddress.zip = enteredAddress.postalCode;
				shipToAddress.postalCode = enteredAddress.postalCode;
			}

			if (props.skipAddressValidation && props.address && props.address.COUNTY) {
				shipToAddress.county = props.address.COUNTY;
			}
		} else {
			const suggestedAddress = addressVerificationData.responseObject || {};
			const country =
				suggestedAddress.country.toUpperCase() === 'UNITED STATES OF AMERICA'
					? 'US'
					: suggestedAddress.country.toUpperCase() === 'CANADA'
					? 'CA'
					: suggestedAddress.country;

			shipToAddress = {
				...shipToAddress,
				addressLine1: suggestedAddress.addressline1,
				addressLine2: suggestedAddress.addressline2,
				addressLine3: suggestedAddress.addressline3,
				city: suggestedAddress.city,
				state: suggestedAddress.state,
				county: suggestedAddress.county,
				country,
				zip: suggestedAddress.postalcode,
				postalCode: suggestedAddress.postalcode
			};

			if (country === 'CA') {
				shipToAddress.province = suggestedAddress.state;
			}
		}

		let firstName, lastName;
		if (props.context === 'NCRP') {
			if (enteredAddress.type === 'RESIDENTIAL') {
				firstName = enteredAddress.name.trim();
				lastName = ' ';
			} else if (enteredAddress.type === 'COMMERCIAL') {
				firstName = enteredAddress.companyName.trim();
				lastName = ' ';
			}
		} else if (enteredAddress.type === 'CONSUMER') {
			const nameSegments = enteredAddress.name.trim().split(/\s+/);

			if (nameSegments.length > 1) {
				firstName = nameSegments.at(0) || '';
				firstName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
				lastName = nameSegments.at(-1) || '';
				lastName = lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase();
			}
		}

		let account;

		if (props.context === 'NCRP') {
			account = user.genericNCRPAccount.accountDetails;
		} else {
			account = user.selectedAccount;
		}

		const payload = {
			firstName,
			lastName,
			accountId: account.ACCOUNTID,
			orgName: account.ORGANIZATIONNAME,
			accountPartyId: account.PARTYID,
			customerClassification: account.CUSTOMERCLASSCODE,
			address1: shipToAddress.addressLine1 && shipToAddress.addressLine1.trim(),
			address2: shipToAddress.addressLine2 ? shipToAddress.addressLine2.trim() : '',
			address3: shipToAddress.addressLine3 ? shipToAddress.addressLine3.trim() : '',
			city: shipToAddress.city,
			state: shipToAddress.state,
			province: shipToAddress.province,
			zip: shipToAddress.zip,
			postalCode: shipToAddress.postalCode,
			country: shipToAddress.country,
			county: shipToAddress.county
		};

		dispatch(
			personShipTo(payload, shipToResponse => {
				if (shipToResponse.status && shipToResponse.responseObject && shipToResponse.responseObject.siteUseId) {
					const retryLimit = 3;

					if (props.context === 'NCRP' || enteredAddress.type === 'CONSUMER') {
						updateConsumerAddress(payload, shipToResponse.responseObject);
					} else {
						updateShipToAddress(
							`accountNumber=${account.ACCOUNTNUMBER}&partyId=${account.PARTYID}&requestType=getAddressList`,
							shipToResponse.responseObject.siteUseId,
							retryLimit
						);
					}
				}
			})
		);
	};

	const handleUseSelectedAddress = () => {
		if (selectedAddress === 'asEntered' && screens.length === 3) {
			setCurrentScreenIndex(2);
		} else {
			createShipToAddress();
		}
	};

	return (
		<div className={'overlayDialog visible'} id="newShipToAddressOverlayDialog">
			<div className="blackoutLayer"></div>
			<div className="overlayDialogWindow">
				<a className="closeButton" onClick={props.onClose}>
					×
				</a>
				<h2 className="title">
					<center>{screens[currentScreenIndex].title}</center>
				</h2>
				{currentScreenIndex === 0 && (
					<form className="newAddressForm" onSubmit={handleSubmit}>
						<ShipToAddress
							ref={addressRef}
							address={screens[0].data}
							context={props.context}
							isInternational={props.limitNewShipToAddressTo === 'INTERNATIONAL'}
							disabledFields={props.disabledFields}
						/>
						<div className="actions">
							<button>Submit</button>
						</div>
					</form>
				)}
				{currentScreenIndex === 1 && (
					<AddressVerificationForm
						onAddressSelection={handleAddressSelection}
						isEnteredAddressCorrect={selectedAddress === 'asEntered'}
						enteredAddress={screens[0].data}
						suggestedAddress={addressVerificationData}
						onEditAddress={handleEditAddress}
						onUseSelectedAddress={handleUseSelectedAddress}
						errors={errorMessages}
						onBack={handleBack}
					/>
				)}
				{currentScreenIndex === 2 && (
					<div>
						<p>Are you sure you want to proceed with the entered address?</p>
						<div className="actions">
							<button type="button" className="orangeButton" onClick={handleBack}>
								Back
							</button>
							<button onClick={createShipToAddress}>Confirm</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default newShipToAddressOverlayDialog;



// WEBPACK FOOTER //
// ./src/App/components/dialogs/newShipToAddressOverlayDialog.jsx